import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { api } from "../lib/api";

/** P3-4 — move stock between bins. Two ledger entries; on hand does not change. */
export default function MoveBinDialog({ projectId, product, onClose, onMoved }) {
  const { user } = useAuth();
  const bins = product?.bins || [];
  const [from, setFrom] = useState(bins[0]?.bin || "");
  const [to, setTo] = useState("");
  const [qty, setQty] = useState(String(bins[0]?.qty || ""));
  const [busy, setBusy] = useState(false);
  if (!product) return null;
  const here = bins.find((b) => b.bin === from);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/on-hand/move-bin", {
        project_id: projectId, product_uid: product.product_uid, product_short: product.product_short || "", uom: product.uom || "",
        from_bin: from, to_bin: to, qty: Number(qty), actor: user?.name || user?.email || "" });
      toast.success(`${product.product_short || "Product"} — ${Number(qty)} moved ${from} → ${to}`);
      onMoved?.(data.bins);
      onClose();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Could not move the stock"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="move-bin-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Move bin — {product.product_short}</DialogTitle>
          <DialogDescription className="text-xs">Stock moves from one bin to another. On hand stays the same; the ledger records both legs.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <select value={from} onChange={(e) => { setFrom(e.target.value); setQty(String(bins.find((b) => b.bin === e.target.value)?.qty || "")); }}
                    data-testid="move-bin-from" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground">
              {bins.map((b) => <option key={b.bin} value={b.bin}>{b.bin} · {b.qty}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Bin" data-testid="move-bin-to" className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Quantity{here ? ` (max ${here.qty})` : ""}</Label>
            <Input type="number" min="0" max={here?.qty} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="move-bin-qty" className="h-9 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="move-bin-cancel">Cancel</Button>
          <Button size="sm" disabled={busy || !from || !to.trim() || !(Number(qty) > 0)} onClick={save} data-testid="move-bin-save">Move</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
